// client/src/utils/visibility.js
// Utility functions for tab visibility and leader election

/**
 * Generate a unique tab ID
 */
export const TAB_ID = Math.random().toString(36).slice(2);

/**
 * Leader election for single-tab polling
 */
const LEADER_KEY = 'velox:poll-leader:contacts';

/**
 * Claim leadership for polling
 * @param {number} ttlMs - Leadership TTL in milliseconds
 * @returns {boolean} True if we became the leader
 */
export function claimLeader(ttlMs = 30000) {
  const now = Date.now();
  const rec = JSON.parse(localStorage.getItem(LEADER_KEY) || 'null');
  
  if (!rec || rec.expiresAt < now) {
    localStorage.setItem(LEADER_KEY, JSON.stringify({ 
      tabId: TAB_ID, 
      expiresAt: now + ttlMs 
    }));
    return true;
  }
  
  return rec.tabId === TAB_ID;
}

/**
 * Renew leadership
 * @param {number} ttlMs - Leadership TTL in milliseconds
 */
export function renewLeader(ttlMs = 30000) {
  const rec = JSON.parse(localStorage.getItem(LEADER_KEY) || 'null');
  if (rec?.tabId === TAB_ID) {
    localStorage.setItem(LEADER_KEY, JSON.stringify({ 
      tabId: TAB_ID, 
      expiresAt: Date.now() + ttlMs 
    }));
  }
}

/**
 * Release leadership
 */
export function releaseLeader() {
  const rec = JSON.parse(localStorage.getItem(LEADER_KEY) || 'null');
  if (rec?.tabId === TAB_ID) {
    localStorage.removeItem(LEADER_KEY);
  }
}

/**
 * Check if we are the current leader
 * @returns {boolean} True if we are the leader
 */
export function isLeader() {
  const rec = JSON.parse(localStorage.getItem(LEADER_KEY) || 'null');
  return rec?.tabId === TAB_ID && rec?.expiresAt > Date.now();
}

/**
 * Add jitter to polling interval
 * @param {number} baseMs - Base interval in milliseconds
 * @param {number} jitterMs - Jitter range in milliseconds
 * @returns {number} Jittered interval
 */
export function addJitter(baseMs, jitterMs = 4000) {
  return baseMs + (Math.random() - 0.5) * 2 * jitterMs;
}

/**
 * Check if tab is visible
 * @returns {boolean} True if tab is visible
 */
export function isTabVisible() {
  return document.visibilityState === 'visible';
}

/**
 * Check if browser is online
 * @returns {boolean} True if online
 */
export function isOnline() {
  return navigator.onLine;
}
